<template>
  <div id="document-history">
    <table-wrapper
      :items="history"
      :fields="tableFields"
      :filter-display="false"
      :loading="isLoading"
      :paginator="false"
    >
      <template #cell(statusChange)="{ data }">
        <template v-if="data">
          <template v-if="data.newCheckBotStatus">
            {{ t(`checkBotStatus.${data.newCheckBotStatus}`) }}
          </template>
          <template v-if="data.newCheckBotInferenceStatus">
            {{
              t(`checkBotInferenceStatus.${data.newCheckBotInferenceStatus}`)
            }}
          </template>
          <template v-if="data.newInspectorStatus">
            {{ t(`inspectorStatus.${data.newInspectorStatus}`) }}
          </template>
          <template v-if="data.newQualityCheckStatus">
            {{ t(`qualityCheckStatus.${data.newQualityCheckStatus}`) }}
          </template>
          <template v-if="data.newMetadataCheckStatus">
            {{ t(`metadataCheckStatus.${data.newMetadataCheckStatus}`) }}
          </template>
          <template v-if="data.recordLibraryTransferStatus">
            {{
              t(
                `recordLibraryTransferStatus.${data.recordLibraryTransferStatus}`,
              )
            }}
          </template>
        </template>
      </template>

      <template #cell(event)="{ data }">
        {{ t(`documentDetailView.history.events.${data?.event}`) }}
      </template>

      <template #cell(actor)="{ data }">
        <template v-if="data?.actor === actorApp">
          {{ t(`documentDetailView.history.${actorApp}`) }}
        </template>
        <template v-else>
          {{ data?.actor }}
        </template>
      </template>
      <template #cell(additionalInfo)="{ data }">
        <span v-if="data.daisyDocIds?.length">
          <Button
            v-for="id in data.daisyDocIds"
            :key="id"
            size="small"
            variant="text"
            severity="secondary"
            @click="openDaisyDoc(id)"
          >
            <img
              :src="DaisyIcon"
              :title="t('documentUpload.openInDaisy')"
              class="w-3.5 h-3.5"
              alt="daisy"
            />
          </Button>
        </span>
        <span v-else>{{ getAdditionalInfoText(data) }}</span>
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import axios, { AxiosResponse } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import {
    ACTOR_APP,
    DocumentHistoryEntry,
    ProjectType,
    DocumentEvent,
  } from 'types';
  import {
    TableFieldsBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { Button } from 'primevue';
  import DaisyIcon from '../../assets/daisy-icon.svg';
  import { prodisPlatformUrls } from '@/static/platform-urls';
  import { useStore } from 'vuex';
  import { IVueexState } from '@/store';

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const store = useStore<IVueexState>();

  const { mode, uploadId, documentId } = defineProps<{
    mode: ProjectType;
    uploadId: string;
    documentId: string;
  }>();

  const history = ref<DocumentHistoryEntry[]>([]);
  const actorApp = ref<string>(ACTOR_APP);

  const { isLoading } = useTableWrapper<DocumentHistoryEntry>(
    TableMode.CLIENT_SIDE,
    'date',
    getHistory,
  );

  const tableFields = computed(() =>
    new TableFieldsBuilder<DocumentHistoryEntry>({
      labelTranslationKey: 'documentDetailView.history',
      sortable: false,
    })
      .addField('date', {
        width: '20%',
        valueGetter: (item) => new Date(item.date).toLocaleString('de-DE'),
      })
      .addField('event', { width: '20%' })
      .addField('actor', { width: '20%' })
      .addField('statusChange' as any, { width: '20%' })
      .addField('additionalInfo' as any, {
        width: '20%',
        truncation: {
          maxLength: 100,
        },
        valueGetter: (item) => getAdditionalInfoText(item),
      })
      .build(),
  );

  async function getHistory() {
    try {
      const endpoint =
        mode === ProjectType.SUPPLIER_PROJECT
          ? endpointsList.documentHistory.getDocumentHistory(
              uploadId,
              documentId,
            )
          : endpointsList.documentHistory.getDigitalizationDocumentHistory(
              uploadId,
              documentId,
            );
      const response: AxiosResponse<DocumentHistoryEntry[]> =
        await axios.get(endpoint);
      history.value = response.data;
    } catch (err: unknown) {
      createErrorToast(
        t('documentDetailView.history.errors.errorGettingHistory'),
      );
    }
  }

  function getAdditionalInfoText(item: DocumentHistoryEntry): string {
    if (item.event === DocumentEvent.INSPECTION && item.inspectorRemarks) {
      return item.inspectorRemarks;
    }
    if (isFailedCheckBotInputHistoryEntry(item)) {
      return getFailureReason(item);
    }
    return '';
  }

  function isFailedCheckBotInputHistoryEntry(data: DocumentHistoryEntry) {
    return data.event === DocumentEvent.CHECKBOT_INPUT_FAILED;
  }

  function getFailureReason(historyEntry: DocumentHistoryEntry): string {
    if (historyEntry.event !== DocumentEvent.CHECKBOT_INPUT_FAILED) return '';
    if (!historyEntry.failureReason) return '';
    return (
      ` ${t('documentDetailView.history.failureReason')}: ` +
      t(
        `documentDetailView.history.failureReasons.${historyEntry.failureReason}`,
      )
    );
  }

  function openDaisyDoc(daisyDocId: string): void {
    const link = `${prodisPlatformUrls[store.state.env!]}/documents/detail/${daisyDocId}`;
    window.open(link, '_blank');
  }
</script>

<style scoped lang="scss">
  #document-history {
    margin-top: 1.875rem;

    #spinner-container {
      width: 100%;
      height: 10vh;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .additional-info-cell {
      cursor: pointer;
    }
  }
</style>
